/**
 * PreviewLink Component
 *
 * Opens the real Toptracer Go client in a separate tab, showing the site's draft
 * brand. The Go client already reads `?brand=draft`, so this needs no change to it
 * and introduces no coupling between the two applications.
 *
 * Its own component rather than an inline anchor because of one job: the preview
 * shows the *saved* draft, so it has to say when the editor holds unsaved edits
 * that will not appear. That single sentence is what keeps a non-live preview
 * honest rather than confusing.
 *
 * Requirements: 10.1, 10.2, 10.3, 10.4, 10.5, 10.6, 10.7, 10.8
 */

import { useId, useState } from 'react';
import { buildPreviewUrl } from '../utils/previewUrl';
import './PreviewLink.css';

/** Just enough of a bay to offer it for selection. */
export interface PreviewBay {
  readonly id: string;
  readonly name: string;
}

export interface PreviewLinkProps {
  readonly appHost: string;
  readonly siteId: string;
  /** False when the site has neither a draft nor a published record. */
  readonly hasBrand: boolean;
  readonly hasUnsavedChanges: boolean;
  readonly bays: readonly PreviewBay[];
}

export function PreviewLink({
  appHost,
  siteId,
  hasBrand,
  hasUnsavedChanges,
  bays,
}: PreviewLinkProps): JSX.Element {
  const selectId = useId();
  const [bayId, setBayId] = useState('');

  const href = buildPreviewUrl({ appHost, siteId, bayId: bayId || undefined });

  return (
    <section className="preview-link">
      <h2 className="preview-link__heading">Preview</h2>

      {hasBrand ? (
        <>
          <p className="preview-link__description">
            Opens Toptracer Go in a new tab showing this site&apos;s saved draft brand.
            Publishing is what makes it live for players.
          </p>

          {bays.length > 0 && (
            <div className="preview-link__bay">
              <label className="preview-link__bay-label" htmlFor={selectId}>
                Bay
              </label>
              <select
                id={selectId}
                className="preview-link__bay-select"
                value={bayId}
                onChange={(event) => setBayId(event.target.value)}
                aria-label="Bay"
              >
                <option value="">No bay (bay selector screen)</option>
                {bays.map((bay) => (
                  <option key={bay.id} value={bay.id}>
                    {bay.name}
                  </option>
                ))}
              </select>
              <p className="preview-link__hint">
                Choose a bay to reach the setup screen, where the splash logo appears.
              </p>
            </div>
          )}

          <a
            className="preview-link__anchor"
            href={href}
            target="_blank"
            /* A different origin: it has no business reaching back into this window. */
            rel="noopener noreferrer"
          >
            Open preview in new tab
          </a>

          {hasUnsavedChanges && (
            <p className="preview-link__unsaved" role="status">
              You have unsaved changes. The preview shows the saved draft, so those
              changes will not appear until you save.
            </p>
          )}
        </>
      ) : (
        <p className="preview-link__description">
          Nothing to preview yet. Save a draft first, then you can see it in Toptracer
          Go before publishing.
        </p>
      )}
    </section>
  );
}
